// Admin UI for the super-admin-managed transactional email templates.
// Left column: selector per template id. Right column: subject + body editor
// with a "copy to clipboard" chip for each available merge-tag variable so
// the admin never has to remember exact `{{tag}}` casing.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Mail, Variable, RotateCcw, Copy, Eye } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Header } from "./WorkspacesTab";
import { useConfirm } from "@/contexts/ConfirmContext";

// Deterministic sample values for the live preview — same keys the backend
// resolver would receive at send-time, filled in with safe demo content.
const SAMPLE = {
  user_name: "Alex",
  user_email: "alex@example.com",
  brand_name: "ACME Studio",
  inviter_name: "Lotte",
  role: "Creator",
  requested_role: "Manager",
  current_role: "Creator",
  action_link: "https://dashboard.thebrandhub.nl/accept-invite?token=…",
  expires_in: "30 minuten",
  message: "Ik wil graag content kunnen aanmaken.",
  from_name: "Lotte",
  new_owner_name: "Jasper",
  old_role: "Viewer",
  new_role: "Creator",
  decision: "goedgekeurd",
  credits: "50",
  amount: "€15,00",
  language: "Spaans",
};

const applyTags = (text, values) =>
  (text || "").replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, k) => values[k] ?? "");

export default function EmailTemplatesTab() {
  const [catalogue, setCatalogue] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [activeKey, setActiveKey] = useState(null);
  const [draft, setDraft] = useState({ subject: "", body_html: "" });
  const [savedDraft, setSavedDraft] = useState({ subject: "", body_html: "" });
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    const { data } = await api.get("/super-admin/email-templates");
    setCatalogue(data.templates);
    setOverrides(data.overrides || {});
    const firstKey = activeKey || Object.keys(data.templates)[0];
    if (firstKey) setActiveKey(firstKey);
  };

  useEffect(() => { refresh().catch(() => toast.error("Kon templates niet laden")); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load draft whenever selection changes.
  useEffect(() => {
    if (!catalogue || !activeKey) return;
    const def = catalogue[activeKey];
    const o = overrides[activeKey] || {};
    const next = {
      subject: o.subject ?? def.default_subject,
      body_html: o.body_html ?? def.default_body,
    };
    setDraft(next);
    setSavedDraft(next);
  }, [activeKey, overrides, catalogue]);

  const dirty = useMemo(() =>
    draft.subject !== savedDraft.subject || draft.body_html !== savedDraft.body_html,
    [draft, savedDraft]);

  const save = async () => {
    if (!activeKey || !dirty) return;
    setSaving(true);
    try {
      await api.put(`/super-admin/email-templates/${activeKey}`, draft);
      toast.success("Template opgeslagen — tenants gebruiken deze tekst direct.");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!activeKey) return;
    const ok = await confirm({
      title: "Terug naar standaard?",
      body: "De aanpassingen aan deze template worden verwijderd en vervangen door de standaard tekst van The Brand Hub.",
      confirmLabel: "Reset",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/super-admin/email-templates/${activeKey}`);
      toast.success("Terug naar standaard");
      await refresh();
    } catch { toast.error("Reset mislukt"); }
  };

  if (!catalogue) return <div className="text-sm text-gray-500">Laden…</div>;

  const current = catalogue[activeKey];
  const hasOverride = Boolean(overrides[activeKey]);

  return (
    <div data-testid="sa-email-templates">
      <Header title="Email Templates" sub="Beheer de teksten die élke tenant hub verstuurt. Merge-tags vul je in met {{variabele}}." />

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6">
        {/* Selector */}
        <aside className="rounded-[var(--global-radius-xl)] border border-gray-200 bg-white overflow-hidden h-fit sticky top-0">
          <div className="px-4 py-3 border-b border-gray-100 text-[11px] uppercase tracking-widest text-gray-400">
            Templates ({Object.keys(catalogue).length})
          </div>
          <nav className="p-2">
            {Object.entries(catalogue).map(([key, def]) => {
              const active = key === activeKey;
              const customised = Boolean(overrides[key]);
              return (
                <button
                  key={key}
                  onClick={() => setActiveKey(key)}
                  data-testid={`sa-template-${key}`}
                  className={`w-full flex items-start gap-2 px-3 py-2.5 rounded-[var(--global-radius)] text-left transition-colors ${
                    active ? "bg-[#1a1a1a] text-white" : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <Mail className={`w-4 h-4 mt-0.5 shrink-0 ${active ? "text-white" : "text-gray-400"}`} strokeWidth={1.75} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate flex items-center gap-1.5">
                      {def.label}
                      {customised && (
                        <span className={`text-[9px] uppercase tracking-widest font-semibold px-1 py-0.5 rounded-sm ${active ? "bg-white/20 text-white" : "bg-emerald-50 text-emerald-700"}`}>
                          Aangepast
                        </span>
                      )}
                    </div>
                    <div className={`text-[11px] mt-0.5 line-clamp-2 ${active ? "text-white/70" : "text-gray-500"}`}>
                      {def.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Editor */}
        <section className="rounded-[var(--global-radius-xl)] border border-gray-200 bg-white p-6">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-1">{activeKey}</div>
              <h2 className="font-display text-2xl font-medium text-[#1a1a1a]">{current.label}</h2>
              <p className="text-sm text-gray-500 mt-1">{current.description}</p>
            </div>
            {hasOverride && (
              <button
                onClick={reset}
                data-testid="sa-template-reset"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[var(--global-radius)] border border-gray-200 bg-white text-xs text-gray-600 hover:text-[#1a1a1a] hover:border-gray-300"
              >
                <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.75} /> Reset
              </button>
            )}
          </div>

          <VariablesHelper vars={current.vars} />

          <div className="space-y-4 mt-5">
            <label className="block">
              <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-1">Onderwerp</div>
              <Input
                value={draft.subject}
                onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                className="bg-white"
                placeholder={current.default_subject}
                data-testid="sa-template-subject"
              />
            </label>

            <label className="block">
              <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-1">
                Body (HTML toegestaan)
              </div>
              <textarea
                value={draft.body_html}
                onChange={(e) => setDraft({ ...draft, body_html: e.target.value })}
                rows={14}
                placeholder={current.default_body}
                className="w-full bg-[#fafafa] border border-gray-200 rounded-[var(--global-radius)] p-3 text-sm font-mono text-[#1a1a1a] resize-y focus:outline-none focus:border-gray-400"
                data-testid="sa-template-body"
              />
            </label>

            <LivePreview subject={draft.subject} bodyHtml={draft.body_html} />
          </div>

          <div className="mt-6 flex justify-end sticky bottom-0 bg-white pt-4 border-t border-gray-100">
            <Button
              onClick={save}
              disabled={!dirty || saving}
              data-testid="sa-template-save"
              className="h-10 px-6 rounded-[var(--global-radius)] bg-[var(--global-action)] text-white hover:opacity-90 disabled:opacity-50"
            >
              {saving ? "Opslaan…" : "Save changes"}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}

function VariablesHelper({ vars }) {
  const copyTag = async (name) => {
    const tag = `{{${name}}}`;
    try { await navigator.clipboard.writeText(tag); toast.success(`${tag} gekopieerd`); }
    catch { /* ignore */ }
  };

  return (
    <div
      data-testid="sa-template-vars"
      className="rounded-[var(--global-radius-lg)] border border-dashed border-gray-300 bg-[#fafafa] p-4"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-500 mb-2">
        <Variable className="w-3.5 h-3.5" strokeWidth={1.75} />
        Available variables
      </div>
      <div className="flex flex-wrap gap-1.5">
        {vars.map((v) => (
          <button
            key={v}
            onClick={() => copyTag(v)}
            data-testid={`sa-template-var-${v}`}
            className="inline-flex items-center gap-1 h-7 px-2 rounded-[var(--global-radius)] bg-white border border-gray-200 font-mono text-[11px] text-[#1a1a1a] hover:border-gray-400"
          >
            {`{{${v}}}`}
            <Copy className="w-3 h-3 text-gray-400" strokeWidth={1.75} />
          </button>
        ))}
      </div>
      <div className="text-[11px] text-gray-500 mt-2">
        Klik een tag om te kopiëren. Plak hem waar je wilt in de tekst — de server vervangt ze automatisch bij verzenden.
      </div>
    </div>
  );
}

function LivePreview({ subject, bodyHtml }) {
  // Wrap the merged inner-body in the same hero-style shell the real email
  // uses, so the admin recognises the result at a glance. We don't re-render
  // the full MJML wrapper (would require pulling the backend render into the
  // bundle); a lightweight visual approximation is enough for a sanity-check.
  const mergedSubject = applyTags(subject, SAMPLE);
  const mergedBody = applyTags(bodyHtml, SAMPLE);
  const srcDoc = `
<!doctype html><html><head><meta charset="utf-8"><style>
body{margin:0;background:#f3f3f1;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#1a1a1a}
.wrap{max-width:520px;margin:16px auto;background:#fff;border-radius:10px;overflow:hidden;border:1px solid #e5e5e5}
.hero{background:#c6dbfc;padding:24px 28px;height:140px;display:flex;flex-direction:column;justify-content:space-between}
.eyebrow{font-size:12px;font-weight:500;opacity:.75}
h1{margin:0;font-size:22px;font-weight:500;letter-spacing:-.3px}
.body{padding:36px 28px 18px;font-size:14px;line-height:1.55}
.greet{font-size:18px;font-weight:500;margin:0 0 18px}
.cta{display:inline-block;margin-top:18px;background:#1a1a1a;color:#fff;padding:10px 20px;border-radius:6px;text-decoration:none;font-size:13px;font-weight:500}
.footer{background:#efefef;color:#6b6b6b;font-size:11px;padding:14px 20px}
</style></head><body>
<div class="wrap">
  <div class="hero"><div class="eyebrow">The Brand Hub</div><h1>${mergedSubject.replace(/</g, "&lt;")}</h1></div>
  <div class="body">
    <p class="greet">Hi ${SAMPLE.user_name},</p>
    <div>${mergedBody}</div>
    <a class="cta" href="#">Open link</a>
  </div>
  <div class="footer">Preview — tenants krijgen deze tekst via The Brand Hub. Verzonden vanuit The Brand Hub.</div>
</div></body></html>`;

  return (
    <div data-testid="sa-template-preview" className="mt-2">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-400 mb-1">
        <Eye className="w-3.5 h-3.5" strokeWidth={1.75} /> Live preview (met voorbeeldwaarden)
      </div>
      <iframe
        title="Email preview"
        srcDoc={srcDoc}
        className="w-full h-[360px] rounded-[var(--global-radius-lg)] border border-gray-200 bg-white"
      />
    </div>
  );
}
